import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  DataTable,
  DataTablePagination,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  AddFilterTrigger,
  FilterBar,
  FilterEditor,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import type { Row, RowSelectionState, Table } from '@tanstack/react-table';
import { formatNumberWithCommas } from '@Utils/utils';
import { CircleAlert, UserIcon } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import type {
  QuickFilterField,
  ResourceTableController,
} from './useResourceTableController';

export interface ResourceMeta {
  count?: number;
  total?: number;
  activeTotal?: number;
  page?: {
    number?: number;
    size?: number;
    pageCount?: number;
  };
}

interface ResourceTableViewProps<TData extends object> {
  label: string; // e.g. 'Members' / 'Customers'
  controller: ResourceTableController<TData>;
  data: TData[];
  meta?: ResourceMeta;
  status: 'idle' | 'pending' | 'error' | 'success';
  defaultRowSelection?: RowSelectionState;

  onRowClick?: (row: TData, table: Table<TData>) => void;
  renderExtraActions?: (rows: TData[]) => React.ReactNode;
  summaryIcon?: React.ReactNode;
  summaryLabel?: string;

  renderFilterBar?: boolean;
  renderQuickFiltersExtra?: (args: {
    controller: ResourceTableController<TData>;
    table: Table<TData>;
  }) => React.ReactNode;
}

export function ResourceTableView<TData extends object>({
  label,
  controller,
  data,
  meta,
  status,
  defaultRowSelection,
  onRowClick,
  renderExtraActions,
  summaryIcon,
  summaryLabel,
  renderFilterBar = true,
  renderQuickFiltersExtra,
}: ResourceTableViewProps<TData>) {
  // console.log('default row selection', defaultRowSelection);
  const {
    columns,
    filterDefinitions,
    filters,
    addOrUpdateFilter,
    removeFilter,
    quickFilterFields,
    quickValues,
    setQuickValue,
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    columnPinning,
    setColumnPinning,
    setServerSorting,
    setServerColumnFilters,
    rowActions,
  } = controller;

  // Add actions column if rowActions is provided
  const columnsWithActions = React.useMemo(() => {
    if (!rowActions) return columns;
    return [
      ...columns,
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: Row<TData> }) => rowActions(row.original),
        enableSorting: false,
        enableColumnFilter: false,
        size: 100,
      },
    ];
  }, [columns, rowActions]);

  const total = meta?.total ?? data.length;

  let activeTotal: number | null = null;
  if (meta?.activeTotal) {
    activeTotal = meta.activeTotal;
  }

  const pageCountFromMeta =
    typeof meta?.page?.pageCount === 'number'
      ? meta!.page!.pageCount
      : undefined;

  const pageCount =
    pageCountFromMeta ?? (total && pageSize ? Math.ceil(total / pageSize) : 1);

  const getRowId = useCallback((row: TData) => {
    const id = (row as any).id;
    if (!id) {
      console.warn('Row missing id property:', row);
    }
    return String(id ?? '');
  }, []);

  const { table, setRowSelection } = useConfiguredTable<TData>({
    data,
    columns: columnsWithActions,
    filters,
    initialSorting: [],
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    columnPinning,
    setColumnPinning,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount,
    onSortingChange: setServerSorting,
    onColumnFiltersChange: setServerColumnFilters,
    autoResetPageOnFilterChange: false,
    getRowId,
  });

  const handleRowClick = useCallback(
    (row: Row<TData>) => {
      if (!onRowClick) return;
      onRowClick(row.original, table);
    },
    [onRowClick, table],
  );

  const lastAppliedSelection = useRef<string | null>(null);
  useEffect(() => {
    if (defaultRowSelection && data && data.length > 0) {
      const selectionKey = JSON.stringify(defaultRowSelection);
      // Only apply if it's different from what we last applied
      if (lastAppliedSelection.current !== selectionKey) {
        setRowSelection(defaultRowSelection);
        lastAppliedSelection.current = selectionKey;
      }
    }
  }, [defaultRowSelection, setRowSelection, data, pageIndex]);

  return (
    <div className="mt-2 space-y-4">
      {/* Summary */}
      <div className="flex h-16 w-full items-center gap-4 rounded-lg bg-zinc-100 p-4 dark:bg-zinc-800">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200 dark:bg-zinc-700">
            {summaryIcon ?? (
              <UserIcon className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
            )}
          </div>
          <div>
            {summaryLabel ?? `Total ${label}: `}
            {status === 'pending' ? (
              <Skeleton className="inline h-6 w-8" />
            ) : (
              <span className="font-bold">
                {formatNumberWithCommas(activeTotal ?? total)}
              </span>
            )}
          </div>
        </div>
        <div className="ml-auto">
          {renderExtraActions && renderExtraActions(data)}
        </div>
      </div>

      {/* Active filters */}
      {renderFilterBar && (
        <FilterBar>
          {filters.map((f) => {
            const def = filterDefinitions.find((d) => d.field === f.field);
            if (!def) return null;
            return (
              <FilterEditor
                key={f.field}
                field={f.field}
                displayName={f.displayName}
                filterItem={f}
                inputType={def.type}
                operators={def.operators}
                applyFilter={addOrUpdateFilter}
                removeFilter={removeFilter}
                description={def.description}
              />
            );
          })}

          <div className="resource-table-add-filter">
            <AddFilterTrigger
              availableFilters={filterDefinitions.filter(
                (def) => !filters.some((f) => f.field === def.field),
              )}
              onAdd={(newFilter) => addOrUpdateFilter(newFilter)}
            />
          </div>
        </FilterBar>
      )}

      {/* Quick filters + extra actions */}
      {quickFilterFields.length > 0 && (
        <div className="flex items-center gap-4">
          {quickFilterFields.map((q: QuickFilterField) => (
            <Input
              key={q.field}
              placeholder={
                q.label ? `Filter by ${q.label.toLowerCase()}...` : 'Filter...'
              }
              value={quickValues[q.field] ?? ''}
              onChange={(e) => setQuickValue(q.field, e.target.value)}
              className="w-64"
            />
          ))}

          {renderQuickFiltersExtra?.({ controller, table })}
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <Alert variant="destructive" className="my-4">
          <CircleAlert />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Error loading {label.toLowerCase()}. Please try again later.
          </AlertDescription>
        </Alert>
      )}

      <div>
        {/* Table */}
        <DataTable
          table={table}
          onRowClick={onRowClick ? handleRowClick : undefined}
          isLoading={status === 'pending'}
          skeletonRowCount={pageSize}
        />

        {/* Pagination */}
        <DataTablePagination
          className="resource-table-pagination mt-0"
          table={table}
          meta={{ total }}
          pagination={{
            size: pageSize,
            number: pageIndex + 1,
          }}
          setPage={({ size, number }) => {
            if (size && size !== pageSize) {
              setPageSize(size);
              setPageIndex(0);
            } else if (number) {
              setPageIndex(number - 1);
            }
          }}
          setPageNumber={(n) => setPageIndex(n - 1)}
          isLoading={status === 'pending'}
        />
      </div>
    </div>
  );
}
